/**
 * Save a cleaned evidence pack to a user-chosen folder on this computer.
 * Server ack is hash + pack id only. Media copies come from IndexedDB, never B2.
 */
import { useEffect, useState } from "react";
import { html } from "./ui.js";
import { api } from "./api.js";
import { appendLocalEvidence, hashLocalPayload } from "./ai-local-evidence.js";
import { listLocalEvidence } from "./cameras-local-vault.js";
import {
  PACK_FORMATS,
  buildPackBytes,
  cleanEventIndex,
  fileNameFor,
} from "./local-evidence-pack.js";
import { cleanDossier } from "./local-evidence-folio.js";

const DIR_DB = "croc.evidence.dir.v1";
const DIR_STORE = "handles";
const DIR_KEY = "export_dir";

function openDirDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DIR_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DIR_STORE)) {
        db.createObjectStore(DIR_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("dir db open failed"));
  });
}

export async function rememberDirectoryHandle(handle) {
  if (!handle) return;
  const db = await openDirDb();
  const tx = db.transaction(DIR_STORE, "readwrite");
  tx.objectStore(DIR_STORE).put(handle, DIR_KEY);
  await new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function restoreDirectoryHandle() {
  if (typeof indexedDB === "undefined") return null;
  const db = await openDirDb();
  const tx = db.transaction(DIR_STORE, "readonly");
  const req = tx.objectStore(DIR_STORE).get(DIR_KEY);
  const handle = await new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  if (!handle) return null;
  if (handle.queryPermission) {
    let perm = await handle.queryPermission({ mode: "readwrite" });
    if (perm !== "granted" && handle.requestPermission) {
      perm = await handle.requestPermission({ mode: "readwrite" });
    }
    if (perm !== "granted") return null;
  }
  return handle;
}

const PACK_STEP = {
  event: "Event",
  team_run: "Team run",
  dispatch: "Dispatch",
  camera: "Camera",
  video: "Live",
  screenshot: "Snapshot",
  recording: "Record",
};

function packCountLine(chain) {
  const rows = Array.isArray(chain) ? chain : [];
  const n = (step) => rows.filter((row) => row && row.step === step).length;
  return `Live ${n("video")} · Snapshot ${n("screenshot")} · Record ${n("recording")}`;
}

function dossierLine(preview) {
  const d = cleanDossier(preview && preview.dossier);
  const first = (d.dispatches || [])[0];
  const wait = first && first.response_seconds != null ? `${first.response_seconds}s` : "—";
  return [
    d.place.address || d.place.skip_reason || "address not surveyed",
    first ? `dispatch ${first.dispatch_id} · ${first.responded ? "responded" : "no response"} · ${wait}` : "no dispatch",
    `AI ${d.ai.live_brain ? "coordinator" : (d.ai.brain_honesty || "none")}`,
    `Human ${d.human.intervened ? (d.human.last_control_verb || "yes") : "none"}`,
    "loop open",
  ].join(" · ");
}

function bytesToDataUri(bytes, mime) {
  const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  let bin = "";
  const step = 0x8000;
  for (let i = 0; i < raw.length; i += step) {
    bin += String.fromCharCode(...raw.subarray(i, i + step));
  }
  return `data:${mime || "application/octet-stream"};base64,${btoa(bin)}`;
}

function canPickFolder() {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

function extOf(row) {
  const mime = String((row && row.mime) || "").toLowerCase();
  if (mime.includes("png")) return "png";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  const kind = String((row && (row.kind || row.media_kind)) || "").toLowerCase();
  if (kind === "recording") return "webm";
  if (kind === "snapshot" || kind === "upload") return "jpg";
  return "bin";
}

function safeName(value, fallback) {
  const t = String(value || "").replace(/[^\w.-]+/g, "_").slice(0, 80);
  return t || fallback;
}

async function writeFile(dir, name, bytes) {
  const file = await dir.getFileHandle(name, { create: true });
  const writable = await file.createWritable();
  await writable.write(bytes);
  await writable.close();
}

async function blobBytes(blob) {
  if (!blob) return null;
  if (blob instanceof Uint8Array) return blob;
  if (typeof blob.arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return null;
}

function downloadFallback(name, bytes, mime) {
  const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function fetchWashedIndex(incidentId) {
  const id = String(incidentId || "").trim();
  if (!id) return cleanEventIndex({});
  try {
    const data = await api(`/incidents/${encodeURIComponent(id)}/evidence/index`);
    return cleanEventIndex((data && data.index) || { event_id: id });
  } catch {
    return cleanEventIndex({ event_id: id });
  }
}

async function localMediaFor(eventId) {
  try {
    const rows = await listLocalEvidence({ limit: 80 });
    const want = String(eventId || "");
    return (rows || []).filter((row) => {
      if (!row || !row.blob) return false;
      if (!want) return true;
      const eid = String(row.event_id || row.loop_id || "");
      return eid === want;
    });
  } catch {
    return [];
  }
}

async function ackServer(incidentId, format, localPackId, contentHash) {
  const id = String(incidentId || "").trim();
  if (!id || id.startsWith("trig:") || id.startsWith("loc:") || id.startsWith("soft-")) {
    return;
  }
  try {
    await api(`/incidents/${encodeURIComponent(id)}/evidence/local-export`, {
      method: "POST",
      body: {
        format,
        local_pack_id: localPackId,
        content_hash: contentHash,
      },
    });
  } catch {
    /* local save already succeeded; ack is optional */
  }
}

async function mediaEmbeds(media) {
  const localFiles = [];
  const embeds = [];
  for (const row of media || []) {
    const raw = await blobBytes(row.blob);
    if (!raw || !raw.length) continue;
    const kind = String(row.kind || row.media_kind || "media");
    const name = `${kind}-${safeName(row.id || row.source_id || "item", "item")}.${extOf(row)}`;
    localFiles.push(name);
    const mime = String(row.mime || (extOf(row) === "png" ? "image/png" : extOf(row) === "jpg" ? "image/jpeg" : ""));
    if (raw.length <= 1200000 && /image|jpg|png|jpeg|snapshot|upload/i.test(`${kind} ${mime}`)) {
      embeds.push({ name, mime: mime || "image/jpeg", dataUri: bytesToDataUri(raw, mime || "image/jpeg"), kind });
    }
  }
  return { localFiles, embeds };
}

export async function saveEvidencePack({
  incidentId,
  eventId,
  format = "html",
  directoryHandle = null,
}) {
  const fmt = PACK_FORMATS[format] ? format : "html";
  const eid = String(eventId || incidentId || "").trim();
  const index = await fetchWashedIndex(incidentId || eid);
  if (!index.event_id) index.event_id = eid;
  const media = await localMediaFor(eid);
  const packed = await mediaEmbeds(media);
  const extras = { local_files: packed.localFiles, embeds: packed.embeds };
  const bytes = buildPackBytes(fmt, index, extras);
  const reportName = fileNameFor(fmt, index.event_id || eid);
  const spec = PACK_FORMATS[fmt];
  const folioName = fileNameFor("html", index.event_id || eid);
  const folioBytes = fmt === "html" ? bytes : buildPackBytes("html", index, extras);

  let usedFolder = false;
  let folderName = "";
  let handle = directoryHandle;
  if (!handle && canPickFolder()) {
    handle = await window.showDirectoryPicker({
      id: "croc-evidence-export",
      mode: "readwrite",
      startIn: "documents",
    });
    await rememberDirectoryHandle(handle);
  }
  if (handle && handle.getDirectoryHandle) {
    const caseDir = await handle.getDirectoryHandle(
      `croc-event-${safeName(index.event_id || eid, "event")}`,
      { create: true },
    );
    await writeFile(caseDir, folioName, folioBytes);
    if (fmt !== "html") await writeFile(caseDir, reportName, bytes);
    for (const row of media) {
      const raw = await blobBytes(row.blob);
      if (!raw || !raw.length) continue;
      const kind = String(row.kind || row.media_kind || "media");
      const name = `${kind}-${safeName(row.id || row.source_id || "item", "item")}.${extOf(row)}`;
      await writeFile(caseDir, name, raw);
    }
    usedFolder = true;
    folderName = handle.name || "chosen folder";
  } else {
    downloadFallback(reportName, bytes, spec.mime);
  }

  const recorded = appendLocalEvidence("operation", {
    incidentId: index.event_id || eid,
    verb: "local_export",
    payload: {
      format: fmt,
      file: usedFolder ? folioName : reportName,
      local_files: packed.localFiles,
      folder: usedFolder,
    },
  });
  if (recorded && recorded.ok) {
    await ackServer(
      incidentId || eid,
      fmt,
      recorded.local_pack_id,
      recorded.content_hash || hashLocalPayload({ format: fmt, file: reportName }),
    );
  }
  return {
    ok: true,
    file: usedFolder ? folioName : reportName,
    folder: usedFolder,
    folder_name: folderName,
    local_files: packed.localFiles,
    fallback_download: !usedFolder,
    server_store: false,
  };
}

export function LocalEvidenceExport({ incidentId, eventId, className = "" }) {
  const [format, setFormat] = useState("html");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [folder, setFolder] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let live = true;
    restoreDirectoryHandle().then((handle) => {
      if (live && handle && handle.name) setFolder(handle.name);
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    fetchWashedIndex(incidentId).then((idx) => {
      if (live) setPreview(idx);
    }).catch(() => {
      if (live) setPreview(null);
    });
    return () => { live = false; };
  }, [incidentId]);

  const onSave = async () => {
    setBusy(true);
    setMsg("");
    try {
      const remembered = await restoreDirectoryHandle().catch(() => null);
      const result = await saveEvidencePack({
        incidentId,
        eventId: eventId || incidentId,
        format,
        directoryHandle: remembered,
      });
      if (result.folder) {
        setFolder(result.folder_name || folder);
        setMsg(`Saved ${result.file}${result.local_files.length ? ` + ${result.local_files.length} media` : ""} on this computer.`);
      } else {
        setMsg(`This browser cannot keep a folder. ${result.file} went to Downloads.`);
      }
    } catch (err) {
      const name = String((err && err.name) || "");
      if (name === "AbortError") setMsg("Save cancelled.");
      else setMsg(String((err && err.message) || err || "save_failed"));
    } finally {
      setBusy(false);
    }
  };

  const onChooseFolder = async () => {
    if (!canPickFolder()) {
      setMsg("This browser cannot pick a folder. Saving still uses a real local file (Downloads).");
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({
        id: "croc-evidence-export",
        mode: "readwrite",
        startIn: "documents",
      });
      await rememberDirectoryHandle(handle);
      setFolder(handle.name || "folder");
      setMsg(`Folder set: ${handle.name}. Next save writes here.`);
    } catch (err) {
      if (String((err && err.name) || "") !== "AbortError") {
        setMsg(String((err && err.message) || err));
      }
    }
  };

  return html`<div className=${`croc-local-export ${className}`.trim()} data-local-export="1">
    <p className="croc-local-export__note">
      Designed case folio stays on this computer. Server keeps IDs only — no B2, no invented map or close.
    </p>
    ${preview
      ? html`<div className="croc-local-export__preview">
          <p className="croc-local-export__counts">${packCountLine(preview.chain)}</p>
          <p className="croc-local-export__dossier">${dossierLine(preview)}</p>
          <ol className="croc-local-export__chain" aria-label="Pack chain">
            ${(preview.chain || []).slice(0, 8).map((row) => html`<li key=${row.step + row.id} data-step=${row.step}>
              ${PACK_STEP[row.step] || row.step}
            </li>`)}
          </ol>
        </div>`
      : null}
    <div className="croc-local-export__row">
      <label>
        <span>Format</span>
        <select value=${format} onChange=${(e) => setFormat(e.target.value)} disabled=${busy}>
          <option value="html">Folio HTML</option>
          <option value="pdf">PDF</option>
          <option value="xlsx">Excel</option>
          <option value="docx">Word</option>
        </select>
      </label>
      <button type="button" className="croc-local-export__folder" onClick=${onChooseFolder} disabled=${busy}>
        ${folder ? `Folder: ${folder}` : "Set local folder"}
      </button>
      <button type="button" className="croc-local-export__save" onClick=${onSave} disabled=${busy}>
        ${busy ? "Saving…" : "Save case folio"}
      </button>
    </div>
    ${msg ? html`<p className="croc-local-export__msg">${msg}</p>` : null}
  </div>`;
}

export default LocalEvidenceExport;
